import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  updateDoc
} from 'firebase/firestore';

/** Preenche permissões novas em perfis antigos (ex.: módulo financeiro). */
function enrichPermissionsWithDefaults(perms) {
  const hasAdmin = perms.accessAdmin === true;
  const hasKeys = perms.viewKeys === true;
  return {
    ...perms,
    viewFinancial: perms.viewFinancial ?? (hasAdmin || hasKeys),
    createFinancial: perms.createFinancial ?? (hasAdmin || hasKeys),
    editFinancial: perms.editFinancial ?? hasAdmin,
    deleteFinancial: perms.deleteFinancial ?? hasAdmin,
    viewFinancialReports: perms.viewFinancialReports ?? (hasAdmin || hasKeys)
  };
}

/** Permissões padrão (todas negadas) — usadas no estado inicial e ao deslogar. */
const DEFAULT_PERMISSIONS = {
  viewProperties: false,
  createProperties: false,
  editProperties: false,
  deleteProperties: false,
  viewBlog: false,
  createBlog: false,
  editBlog: false,
  publishBlog: false,
  deleteBlog: false,
  viewBlogReports: false,
  viewUsers: false,
  createUsers: false,
  editUsers: false,
  changeUserType: false,
  deleteUsers: false,
  viewUsersReports: false,
  viewCharts: false,
  accessChat: false,
  accessCRM: false,
  viewCRMReports: false,
  // Leads
  viewLeads: false,
  editLeads: false,
  assignBrokerLeads: false,
  // Pipeline
  viewPipeline: false,
  editCRMColumns: false,
  editCRMLossReasons: false,
  // Clientes
  viewClients: false,
  editClients: false,
  deleteClients: false,
  // Propostas
  viewProposals: false,
  createProposals: false,
  editProposals: false,
  deleteProposals: false,
  // Landing Pages
  viewLandingPagesList: false,
  viewLandingPagesReports: false,
  createLandingPages: false,
  editLandingPages: false,
  deleteLandingPages: false,
  // Outras
  viewKeys: false,
  createKeys: false,
  editKeys: false,
  deleteKeys: false,
  manageKeyMovements: false,
  viewKeysReports: false,
  viewFinancial: false,
  createFinancial: false,
  editFinancial: false,
  deleteFinancial: false,
  viewFinancialReports: false,
  viewPropertiesReports: false,
  viewPropertiesIntegrations: false,
  accessProfiles: false,
  accessLogs: false,
  accessSettings: false,
  accessAdmin: false,
  accessAIAssistant: false,
  accessAIExtractor: false,
  accessAIGenerator: false,
  accessAIAnalytics: false,
  accessAIUsage: false
};

export const usePermissions = () => {
  const { user, loading: authLoading } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [refreshKey, setRefreshKey] = useState(0);

  const reloadPermissions = useCallback(() => {
    setRefreshKey((current) => current + 1);
  }, []);

  useEffect(() => {
    const loadUserPermissions = async () => {
      if (authLoading) {
        setLoading(true);
        return;
      }

      if (!user) {
        // Ao deslogar, zera permissões e perfil para que elementos gated
        // (ex.: botão flutuante de IA) desapareçam imediatamente, sem refresh.
        setPermissions(DEFAULT_PERMISSIONS);
        setUserProfile(null);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const db = getFirestore();
        
        // Busca dados do usuário no Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        
        if (!userData) {
          setLoading(false);
          return;
        }

        const applyProfilePermissions = async (profileDoc, { linkUser = false } = {}) => {
          const profileData = profileDoc.data();
          if (!profileData?.permissions) return false;

          setUserProfile(profileData);
          setPermissions(enrichPermissionsWithDefaults(profileData.permissions));

          if (linkUser) {
            try {
              await updateDoc(doc(db, 'users', user.uid), {
                profileId: profileDoc.id
              });
            } catch (error) {
              console.error('Erro ao atualizar profileId:', error);
            }
          }

          return true;
        };

        const loadProfileByName = async (profileName, linkUser = false) => {
          const profilesQuery = query(collection(db, 'profiles'), where('name', '==', profileName));
          const profilesSnapshot = await getDocs(profilesQuery);
          if (profilesSnapshot.empty) return false;
          return applyProfilePermissions(profilesSnapshot.docs[0], { linkUser });
        };

        let permissionsLoaded = false;

        // 1) Perfil vinculado diretamente ao usuário
        if (userData.profileId) {
          const profileDoc = await getDoc(doc(db, 'profiles', userData.profileId));
          if (profileDoc.exists()) {
            permissionsLoaded = await applyProfilePermissions(profileDoc);
          }
        }

        // 2) Perfil pelo role
        if (!permissionsLoaded && userData.role) {
          const roleToProfileMap = {
            broker: 'Corretor',
            client: 'Cliente',
            admin: 'Administrador'
          };
          const profileName = roleToProfileMap[userData.role] || userData.role;
          permissionsLoaded = await loadProfileByName(profileName, true);
        }

        // 3) Admin legado (tag ADM) — usa perfil Administrador do Firestore, não permissões fixas
        const isLegacyAdminUser =
          userData.tag === 'ADM' || userData.tag === 'ADMIN' || userData.isAdmin === true;

        if (!permissionsLoaded && isLegacyAdminUser) {
          permissionsLoaded = await loadProfileByName('Administrador', true);
        }

        // 4) Fallback legado sem perfil cadastrado
        if (!permissionsLoaded && isLegacyAdminUser) {
          setPermissions({
            viewProperties: true,
            createProperties: true,
            editProperties: true,
            deleteProperties: true,
            viewBlog: true,
            createBlog: true,
            editBlog: true,
            publishBlog: true,
            deleteBlog: true,
            viewBlogReports: true,
            viewUsers: true,
            createUsers: true,
            editUsers: true,
            changeUserType: true,
            deleteUsers: true,
            viewUsersReports: true,
            viewCharts: true,
            accessChat: true,
            accessCRM: true,
            viewCRMReports: true,
            viewLeads: true,
            editLeads: true,
            assignBrokerLeads: true,
            viewPipeline: true,
            editCRMColumns: true,
            editCRMLossReasons: true,
            viewKeys: true,
            createKeys: true,
            editKeys: true,
            deleteKeys: true,
            manageKeyMovements: true,
            viewKeysReports: true,
            viewFinancial: true,
            createFinancial: true,
            editFinancial: true,
            deleteFinancial: true,
            viewFinancialReports: true,
            viewPropertiesReports: true,
            viewPropertiesIntegrations: false,
            viewLandingPagesList: true,
            viewLandingPagesReports: true,
            createLandingPages: true,
            editLandingPages: true,
            deleteLandingPages: true,
            accessProfiles: true,
            accessLogs: true,
            accessSettings: true,
            accessAdmin: true,
            accessAIAssistant: false,
            accessAIExtractor: false,
            accessAIGenerator: false,
            accessAIAnalytics: false,
            accessAIUsage: false
          });
          setUserProfile({ name: 'Administrador (Legado)', permissions: {} });
        }
      } catch (error) {
        console.error('Erro ao carregar permissões:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserPermissions();
  }, [user, authLoading, refreshKey]);

  // Funções auxiliares para verificar permissões específicas
  const canAccessAdmin = () => {
    // Verifica se tem permissão no perfil OU se é admin legado
    const hasProfilePermission = permissions.accessAdmin === true;
    const isLegacyAdmin = user?.isAdmin === true;
    const canAccess = hasProfilePermission || isLegacyAdmin;
    
    return canAccess;
  };

  const canViewProperties = () => {
    return permissions.viewProperties;
  };

  const canCreateProperties = () => {
    return permissions.createProperties;
  };

  const canEditProperties = () => {
    return permissions.editProperties;
  };

  const canDeleteProperties = () => {
    return permissions.deleteProperties;
  };

  const canViewBlog = () => {
    return permissions.viewBlog;
  };

  const canCreateBlog = () => {
    return permissions.createBlog;
  };

  const canEditBlog = () => {
    return permissions.editBlog;
  };

  const canPublishBlog = () => {
    return permissions.publishBlog;
  };

  const canDeleteBlog = () => {
    return permissions.deleteBlog;
  };

  const canViewBlogReports = () => {
    return permissions.viewBlogReports;
  };

  const canViewUsers = () => {
    return permissions.viewUsers;
  };

  const canCreateUsers = () => {
    return permissions.createUsers;
  };

  const canEditUsers = () => {
    return permissions.editUsers;
  };

  const canChangeUserType = () => {
    return permissions.changeUserType;
  };

  const canDeleteUsers = () => {
    return permissions.deleteUsers;
  };

  const canViewUsersReports = () => {
    return permissions.viewUsersReports;
  };

  const canViewCharts = () => {
    return permissions.viewCharts;
  };

  const canAccessChat = () => {
    return permissions.accessChat;
  };

  const canAccessCRM = () => {
    return permissions.accessCRM;
  };

  const canViewCRMReports = () => {
    return permissions.viewCRMReports;
  };

  // Leads
  const canViewLeads = () => {
    return permissions.viewLeads;
  };

  const canEditLeads = () => {
    return permissions.editLeads;
  };

  const canAssignBrokerLeads = () => {
    return permissions.assignBrokerLeads;
  };

  // Mantém compatibilidade com código antigo (deprecated)
  const canAccessLeads = () => {
    return permissions.viewLeads || permissions.editLeads || permissions.assignBrokerLeads;
  };

  // Pipeline
  const canViewPipeline = () => {
    return permissions.viewPipeline;
  };

  const canAccessPipeline = () => {
    return permissions.viewPipeline;
  };

  const canEditCRMColumns = () => {
    return permissions.editCRMColumns;
  };

  const canEditCRMLossReasons = () => {
    return permissions.editCRMLossReasons;
  };

  // Clientes
  const canViewClients = () => {
    return permissions.viewClients;
  };

  const canEditClients = () => {
    return permissions.editClients;
  };

  const canDeleteClients = () => {
    return permissions.deleteClients;
  };

  const canAccessClients = () => {
    return permissions.viewClients || permissions.editClients || permissions.deleteClients;
  };

  // Propostas
  const canViewProposals = () => {
    return permissions.viewProposals;
  };

  const canCreateProposals = () => {
    return permissions.createProposals;
  };

  const canEditProposals = () => {
    return permissions.editProposals;
  };

  const canDeleteProposals = () => {
    return permissions.deleteProposals;
  };

  const canAccessProposals = () => {
    return permissions.viewProposals || permissions.createProposals || permissions.editProposals || permissions.deleteProposals;
  };

  const canAccessSettings = () => {
    return permissions.accessSettings;
  };

  const canAccessProfiles = () => {
    return permissions.accessProfiles || permissions.accessAdmin || user?.isAdmin === true;
  };

  const canAccessLogs = () => {
    return permissions.accessLogs;
  };

  const canAccessAIAssistant = () => permissions.accessAIAssistant === true;

  const canAccessAIExtractor = () => permissions.accessAIExtractor === true;

  const canAccessAIGenerator = () => permissions.accessAIGenerator === true;

  const canAccessAIAnalytics = () => permissions.accessAIAnalytics === true;

  const canAccessAIUsage = () => permissions.accessAIUsage === true;

  const canViewKeys = () => {
    return permissions.viewKeys;
  };

  const canCreateKeys = () => {
    return permissions.createKeys;
  };

  const canEditKeys = () => {
    return permissions.editKeys;
  };

  const canDeleteKeys = () => {
    return permissions.deleteKeys;
  };

  const canManageKeyMovements = () => {
    return permissions.manageKeyMovements;
  };

  const canViewKeysReports = () => {
    return permissions.viewKeysReports;
  };

  const hasFinancialAccess = () => {
    return (
      permissions.viewFinancial === true ||
      permissions.accessAdmin === true ||
      user?.isAdmin === true
    );
  };

  const canViewFinancial = () => hasFinancialAccess();

  const canCreateFinancial = () => {
    return hasFinancialAccess() && (permissions.createFinancial === true || permissions.accessAdmin === true || user?.isAdmin === true);
  };

  const canEditFinancial = () => {
    return hasFinancialAccess() && (permissions.editFinancial === true || permissions.accessAdmin === true || user?.isAdmin === true);
  };

  const canDeleteFinancial = () => {
    return hasFinancialAccess() && (permissions.deleteFinancial === true || permissions.accessAdmin === true || user?.isAdmin === true);
  };

  const canViewFinancialReports = () => {
    return (
      permissions.viewFinancialReports === true ||
      permissions.viewFinancial === true ||
      permissions.accessAdmin === true ||
      user?.isAdmin === true
    );
  };

  const canViewPropertiesReports = () => {
    return permissions.viewPropertiesReports;
  };

  const canViewPropertiesIntegrations = () => permissions.viewPropertiesIntegrations === true;

  // Função genérica para verificar qualquer permissão
  const hasPermission = (permission) => {
    // Para acesso ao admin, verifica também se é admin legado
    if (permission === 'accessAdmin') {
      return permissions[permission] === true || user?.isAdmin === true;
    }
    return permissions[permission] === true;
  };

  // Função para verificar múltiplas permissões (todas devem ser true)
  const hasAllPermissions = (permissionList) => {
    return permissionList.every(permission => permissions[permission] === true);
  };

  // Função para verificar múltiplas permissões (pelo menos uma deve ser true)
  const hasAnyPermission = (permissionList) => {
    return permissionList.some(permission => permissions[permission] === true);
  };

  return {
    user,
    userProfile,
    permissions,
    loading,
    canAccessAdmin,
    canViewProperties,
    canCreateProperties,
    canEditProperties,
    canDeleteProperties,
    canViewBlog,
    canCreateBlog,
    canEditBlog,
    canPublishBlog,
    canDeleteBlog,
    canViewBlogReports,
    canViewUsers,
    canCreateUsers,
    canEditUsers,
    canChangeUserType,
    canDeleteUsers,
    canViewUsersReports,
    canViewCharts,
    canAccessChat,
    canAccessCRM,
    canViewCRMReports,
    // Leads
    canViewLeads,
    canEditLeads,
    canAssignBrokerLeads,
    canAccessLeads, // Deprecated - mantido para compatibilidade
    // Pipeline
    canViewPipeline,
    canAccessPipeline,
    canEditCRMColumns,
    canEditCRMLossReasons,
    // Clientes
    canViewClients,
    canEditClients,
    canDeleteClients,
    canAccessClients,
    // Propostas
    canViewProposals,
    canCreateProposals,
    canEditProposals,
    canDeleteProposals,
    canAccessProposals,
    // Outras
    canViewKeys,
    canCreateKeys,
    canEditKeys,
    canDeleteKeys,
    canManageKeyMovements,
    canViewKeysReports,
    canViewFinancial,
    canCreateFinancial,
    canEditFinancial,
    canDeleteFinancial,
    canViewFinancialReports,
    canViewPropertiesReports,
    canViewPropertiesIntegrations,
    canAccessProfiles,
    canAccessLogs,
    canAccessSettings,
    canAccessAIAssistant,
    canAccessAIExtractor,
    canAccessAIGenerator,
    canAccessAIAnalytics,
    canAccessAIUsage,
    reloadPermissions,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission
  };
};
